import { useSortable } from '@dnd-kit/sortable';
import { useLiveData, useService } from '@toeverything/infra';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import type { SetStateAction } from 'jotai';
import type {
  Dispatch,
  HTMLAttributes,
  PropsWithChildren,
  RefObject,
} from 'react';
import {
  memo,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { View } from '../../entities/view';
import { WorkbenchService } from '../../services/workbench';
import { SplitViewIndicator } from './indicator';
import * as styles from './split-view.css';

export interface SplitViewPanelProps
  extends PropsWithChildren<HTMLAttributes<HTMLDivElement>> {
  view: View;
  resizeHandle?: React.ReactNode;
  setSlots?: Dispatch<
    SetStateAction<Record<string, RefObject<HTMLDivElement | null>>>
  >;
}

export const SplitViewPanel = memo(function SplitViewPanel({
  children,
  view,
  setSlots,
}: SplitViewPanelProps) {
  const [indicatorPressed, setIndicatorPressed] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const size = useLiveData(view.size$);
  const isAnimating = useLiveData(view.isAnimating$);
  const workbench = useService(WorkbenchService).workbench;
  const activeView = useLiveData(workbench.activeView$);
  const views = useLiveData(workbench.views$);
  const isLast = views[views.length - 1] === view;

  const {
    attributes,
    listeners,
    transform,
    transition,
    isDragging: dndIsDragging,
    setNodeRef,
  } = useSortable({ id: view.id, attributes: { role: 'group' } });

  const isDragging = dndIsDragging || indicatorPressed;
  const isActive = activeView === view;

  useLayoutEffect(() => {
    if (ref.current) {
      setSlots?.(slots => ({ ...slots, [view.id]: ref }));
    }

    return () => {
      setSlots?.(slots => {
        if (!(view.id in slots)) return slots;
        const next = { ...slots };
        delete next[view.id];
        return next;
      });
    };
  }, [setSlots, view.id]);

  const style = useMemo(
    () => ({
      ...assignInlineVars({ '--size': size.toString() }),
    }),
    [size]
  );
  const dragStyle = useMemo(
    () => ({
      transform: `translate3d(${transform?.x ?? 0}px, ${
        transform?.y ?? 0
      }px, 0)`,
      transition,
    }),
    [transform, transition]
  );

  return (
    <div
      style={style}
      className={styles.splitViewPanel}
      data-is-dragging={isDragging}
      data-is-animating={isAnimating}
      data-is-active={isActive && views.length > 1}
      data-is-last={isLast}
      data-view-id={view.id}
    >
      <div
        ref={setNodeRef}
        style={dragStyle}
        className={styles.splitViewPanelDrag}
        {...attributes}
      >
        <div className={styles.splitViewPanelContent} ref={ref} />
        {views.length > 1 ? (
          <SplitViewIndicator
            listeners={listeners}
            isDragging={isDragging}
            isActive={isActive}
            setPressed={setIndicatorPressed}
          />
        ) : null}
      </div>
      {children}
    </div>
  );
});
